import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Collapse, Row, Spinner, UncontrolledTooltip } from 'reactstrap';
import FormActions from 'views/components/FormActions';
import ContactsDropdown from 'views/formComponents/ContactsDropdown';
import DateInput from 'views/formComponents/DateInput';
import DropdownInput from 'views/formComponents/DropdownInput';
import TextInput from 'views/formComponents/TextInput';
import Icon from '../../../../../utility/icomoon';
import * as unitActions from '../../../Units/actions';
import * as unitSelectors from '../../../Units/reducers';

/* -------------------------------------------------------------------------- */
/*                       SlidingPaneShareUnitLocation                         */
/* -------------------------------------------------------------------------- */

interface Props {
  closeSlidingPane: Function;
  isSlidingPaneOpen: boolean;
  unit: any;
}

const SlidingPaneShareUnitLocation = (props: Props) => {
  const { closeSlidingPane, isSlidingPaneOpen, unit } = props;

  //HOOKS
  const { control, watch, handleSubmit, reset } = useForm();
  const intl = useIntl();
  const dispatch = useDispatch();

  const formValues = watch();
  const [isLinkCreated, setIsLinkCreated] = useState(false);
  const [showDateInput, setShowDateInput] = useState(false);

  const { isCreatingSharedLink, sharedLinkUnit } = useSelector((state) => ({
    sharedLinkUnit: unitSelectors.sharedLinkUnit(state),
    isCreatingSharedLink: unitSelectors.isCreatingSharedLinkUnit(state)
  }));

  //TOPS
  const durations = [
    {
      id: 1,
      value: 1,
      label: `1 ${intl.formatMessage({ id: 'common.hour' })}`,
      label_intl: `1 ${intl.formatMessage({ id: 'common.hour' })}`
    },
    {
      id: 8,
      value: 8,
      label: `8 ${intl.formatMessage({ id: 'common.hours2' })}`,
      label_intl: `8 ${intl.formatMessage({ id: 'common.hours2' })}`
    },
    {
      id: 12,
      value: 12,
      label: `12 ${intl.formatMessage({ id: 'common.hours2' })}`,
      label_intl: `12 ${intl.formatMessage({ id: 'common.hours2' })}`
    },
    {
      id: 24,
      value: 24,
      label: `24 ${intl.formatMessage({ id: 'common.hours2' })}`,
      label_intl: `24 ${intl.formatMessage({ id: 'common.hours2' })}`
    }
  ];

  const onSubmit = () => {
    if (showDateInput && formValues?.linkExpirationDate) {
      dispatch(
        unitActions.startCreatingShareLink(
          unit?.unitId,
          unit?.unitName,
          formValues?.receivers,
          moment(formValues?.linkExpirationDate[1], 'YYYY-MM-DD HH:mm').diff(moment(), 'seconds')
        )
      );
    } else {
      dispatch(
        unitActions.startCreatingShareLink(
          unit?.unitId,
          unit?.unitName,
          formValues?.receivers,
          formValues?.durationId * 3600
        )
      );
    }
  };

  useEffect(() => {
    setIsLinkCreated(false);
    if (sharedLinkUnit !== '' && sharedLinkUnit !== undefined && sharedLinkUnit !== null) {
      setIsLinkCreated(true);
      reset({
        ...formValues,
        sharedLink: sharedLinkUnit
      });
    }
  }, [sharedLinkUnit]);

  return (
    unit && (
      <Fragment>
        <SlidingPane
          className="custom-sliding-pane"
          overlayClassName={'custom-overlay-sliding-pane-map'}
          isOpen={isSlidingPaneOpen}
          title={intl.formatMessage({ id: 'units.shareLinkWithUnitLocation' })}
          subtitle={
            <>
              <div style={{ textAlign: 'center' }}>
                <span>{unit?.unitName}</span>
                <br />
                <span>{unit?.locationName}</span>
              </div>
            </>
          }
          width={'45rem'}
          onRequestClose={() => {
            if (closeSlidingPane) closeSlidingPane();
            dispatch(unitActions.clearSharedLink());
          }}
        >
          <Fragment>
            <Row>
              <Col sm="8">
                {showDateInput ? (
                  <Controller
                    name="linkExpirationDate"
                    control={control}
                    defaultValue={[
                      moment().format('YYYY-MM-DD HH:mm'),
                      moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
                    ]}
                    render={({ field, fieldState }) => (
                      <DateInput
                        field={field}
                        fieldstate={fieldState}
                        label={intl.formatMessage({ id: 'units.expirationDate' })}
                        showTime
                        enableTime
                        minDate={moment().format('YYYY-MM-DD HH:mm:ss')}
                        maxDate={moment().add(7, 'days').format('YYYY-MM-DD HH:mm:ss')}
                        isRange
                      />
                    )}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="durationId"
                    defaultValue={1}
                    render={({ field, fieldState }) => (
                      <DropdownInput
                        field={field}
                        fieldstate={fieldState}
                        id={'durationDropdown'}
                        label={intl.formatMessage({ id: 'units.linkDuration' })}
                        options={durations}
                      />
                    )}
                  />
                )}
              </Col>
              <Col sm="4">
                <Button.Ripple
                  className="mb-0"
                  color="secondary"
                  style={{ marginTop: '18px', marginLeft: '26px' }}
                  /*type="submit"*/
                  onClick={() => setShowDateInput((prev) => !prev)}
                >
                  <FormattedMessage id="common.personalized" />
                </Button.Ripple>
              </Col>
              <Col sm="12">
                <ContactsDropdown
                  control={control}
                  id={'contactEmailInput'}
                  name={'receivers'}
                  isInModal
                  shouldUnregister={true}
                  required
                />
              </Col>
            </Row>
            {isCreatingSharedLink ? (
              <Row>
                <Col sm="12" className="grid-spinner">
                  <Spinner color="primary" size="lg" />
                </Col>
              </Row>
            ) : (
              <Collapse isOpen={isLinkCreated}>
                <Row>
                  <Col sm="12">
                    <Controller
                      control={control}
                      name="sharedLink"
                      render={({ field, fieldState }) => (
                        <TextInput
                          field={field}
                          fieldstate={fieldState}
                          id={'sharedLinkInput'}
                          label={intl.formatMessage({
                            id: 'common.link'
                          })}
                          disabled
                          prependcomponent={
                            <>
                              <UncontrolledTooltip placement={'top'} target={'copyClipboardButton'}>
                                {intl.formatMessage({ id: 'common.copyClipboard' })}
                              </UncontrolledTooltip>
                              <Icon
                                size={20}
                                icon="Clipboard"
                                id={'copyClipboardButton'}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  navigator.clipboard.writeText(formValues?.sharedLink)
                                }
                              />
                            </>
                          }
                        />
                      )}
                    />
                  </Col>
                </Row>
              </Collapse>
            )}
            <FormActions
              variant="footer"
              saveType="button"
              saveLabel={<FormattedMessage id="common.send" />}
              cancelLabel={<FormattedMessage id="common.clean" />}
              onSave={handleSubmit((values) => onSubmit())}
              onCancel={() => {
                setShowDateInput(false);
                reset({
                  durationId: 1,
                  linkExpirationDate: moment().add(1, 'hour').format('YYYY-MM-DD HH:mm')
                });
                dispatch(unitActions.clearSharedLink());
              }}
            />
          </Fragment>
        </SlidingPane>
      </Fragment>
    )
  );
};

SlidingPaneShareUnitLocation.defaultProps = {
  closeSlidingPane: () => null,
  isSlidingPaneOpen: false,
  unit: {}
};

export default SlidingPaneShareUnitLocation;
